/* ============================================================================
   IndexedKpiBadge — visible "% of GO box deep-indexed" KPI.

   Activation rule (per the April 2026 spec):
     Only render when the user has completed a first full box sweep. Until
     boxScanCompleted is true we have no real denominator (true box capacity)
     and showing a fake percentage would be misleading. We hide silently in
     that pre-state so the surface stays clean.

   Math (delegated to deepRecordRatio in lib/deepCaptureSearch):
     numerator   = boxRecords with captureCompleteness === "complete"
                   (records with deep IV + move data, from CSV import or 2-snap)
     denominator = stats.totalBoxSize (the trainer's actual box capacity)
     pct         = numerator / denominator, clamped to 100

   This is a rough estimate by design. As more deep records flow in (CSV
   imports, 2-snap captures), the ratio climbs in real time.

   Variants:
     - "pill"  : compact header chip with a tiny progress arc
     - "card"  : full-width card with progress bar + verbose label
============================================================================ */

import { useMemo } from "react";
import { Database, ArrowRight } from "lucide-react";
import { Link } from "wouter";

import { useInventory } from "@/components/InventoryProvider";
import { deepRecordRatio } from "@/lib/deepCaptureSearch";

// ── Shared math hook ─────────────────────────────────────────────────────

function useIndexedKpi() {
  const { boxRecords, boxScanCompleted, stats } = useInventory();
  const ratio = useMemo(
    () => deepRecordRatio(boxRecords, stats.totalBoxSize),
    [boxRecords, stats.totalBoxSize],
  );
  return {
    active: boxScanCompleted && ratio.total > 0,
    done: ratio.done,
    total: ratio.total,
    pct: ratio.pct,
  };
}

// ── Pill variant, compact for headers ────────────────────────────────────

export function IndexedKpiPill({
  className = "",
  showLabel = true,
}: {
  className?: string;
  showLabel?: boolean;
}) {
  const k = useIndexedKpi();
  if (!k.active) return null;

  // Tiny circular progress: stroke-dashoffset trick on an SVG circle.
  const radius = 7.5;
  const circumference = 2 * Math.PI * radius;
  const dash = (k.pct / 100) * circumference;

  return (
    <Link
      href="/inventory"
      data-testid="kpi-indexed-pill"
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-bold no-underline hover-elevate ${className}`}
      style={{
        background: "var(--accent-fill-glow-faint)",
        border: "1px solid var(--accent-fill-glow)",
        color: "hsl(var(--primary))",
      }}
      title={`${k.done.toLocaleString()} of ${k.total.toLocaleString()} deep-indexed`}
    >
      <svg width="18" height="18" viewBox="0 0 18 18" className="shrink-0">
        <circle
          cx="9" cy="9" r={radius}
          fill="none"
          stroke="var(--accent-fill-glow-subtle)"
          strokeWidth="2"
        />
        <circle
          cx="9" cy="9" r={radius}
          fill="none"
          stroke="var(--accent-fill)"
          strokeWidth="2"
          strokeDasharray={`${dash} ${circumference}`}
          strokeLinecap="round"
          transform="rotate(-90 9 9)"
        />
      </svg>
      <span className="tabular-nums">{k.pct.toFixed(0)}%</span>
      {showLabel && <span className="font-semibold opacity-80">indexed</span>}
    </Link>
  );
}

// ── Card variant, anchor for the Profile Connect surface ──────────────────

export function IndexedKpiCard({ className = "" }: { className?: string }) {
  const k = useIndexedKpi();
  if (!k.active) return null;

  // Plain-English status copy that scales with progress.
  const stage =
    k.pct >= 90
      ? "Box almost fully indexed"
      : k.pct >= 50
      ? "Halfway through your box"
      : k.pct >= 20
      ? "Building out your Deep Index"
      : "Just getting started";

  return (
    <Link
      href="/inventory#deep-capture-generator"
      data-testid="kpi-indexed-card"
      className={`block rounded-2xl p-4 hover-elevate no-underline text-foreground ${className}`}
      style={{
        background:
          "linear-gradient(135deg, var(--accent-fill-glow-faint), var(--surface-elev-1) 70%)",
        border: "1px solid var(--accent-fill-glow)",
      }}
    >
      <div className="flex items-center gap-3">
        {/* Progress ring */}
        <div className="relative w-14 h-14 shrink-0">
          <svg width="56" height="56" viewBox="0 0 56 56">
            <circle
              cx="28" cy="28" r="24"
              fill="none"
              stroke="var(--accent-fill-glow-faint)"
              strokeWidth="5"
            />
            <circle
              cx="28" cy="28" r="24"
              fill="none"
              stroke="var(--accent-fill)"
              strokeWidth="5"
              strokeDasharray={`${(k.pct / 100) * 2 * Math.PI * 24} ${
                2 * Math.PI * 24
              }`}
              strokeLinecap="round"
              transform="rotate(-90 28 28)"
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <span
              className="text-sm font-extrabold tabular-nums"
              style={{ color: "hsl(var(--primary))" }}
              data-testid="kpi-indexed-pct"
            >
              {k.pct.toFixed(0)}%
            </span>
          </div>
        </div>

        <div className="min-w-0 flex-1">
          <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-[hsl(var(--primary))] inline-flex items-center gap-1.5">
            <Database className="w-3 h-3" />
            GO box, deep-indexed
          </p>
          <p className="text-sm font-bold leading-tight mt-1">{stage}</p>
          <p className="text-[11px] text-muted-foreground mt-0.5 leading-snug">
            {k.done.toLocaleString()} of {k.total.toLocaleString()} tiles have IVs,
            moves and metadata. Add deep details to climb the bar.
          </p>
        </div>

        <ArrowRight className="w-4 h-4 text-muted-foreground shrink-0" />
      </div>

      {/* Linear bar mirrors the ring for at-a-glance reading */}
      <div
        className="mt-3 h-1.5 rounded-full overflow-hidden"
        style={{ background: "var(--surface-elev-2)" }}
      >
        <div
          className="h-full rounded-full transition-[width] duration-500"
          style={{
            width: `${Math.max(k.pct, 0.4)}%`,
            background:
              "linear-gradient(90deg, var(--accent-fill), var(--accent-fill))",
          }}
        />
      </div>
    </Link>
  );
}
